import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import type { PropsWithChildren } from "react";
import React, { useMemo, useState } from "react";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import type { EntityTag } from "../store/state/messages/types";

export interface InboxCriteria {
  page: number;
  size: number;
  investors?: string[];
  funds?: string[];
  entities?: EntityTag[];
  categories?: string[];
  from?: string;
  to?: string;
  query?: string;
  descriptiveDate?: DescriptiveDateRange;
  isImportant?: boolean;
  preload: boolean;
  selectedInvestorTag?: string;
}

export interface InboxUiState {
  total?: number;
  messagesLoading: boolean;
  messageDetailLoading: boolean;
  messageDetailLoadingError?: boolean;
}

export interface InboxState {
  messageId?: string;
  setMessageId: (id: string) => void;
  criteria: InboxCriteria;
  ui: InboxUiState;
  updateCriteria: (value: Partial<InboxCriteria>) => void;
  updateCriteriaPreload: (value: Partial<InboxCriteria>) => void;
  updateUi: (value: Partial<InboxUiState>) => void;
}

export interface InboxStateContextProps extends InboxState {
  selection: GridRowSelectionModel;
  setSelection: (values: GridRowSelectionModel) => void;
  watermarkInvestorName?: string;
}

const InboxStateContext = React.createContext<InboxStateContextProps | undefined>(undefined);

interface Props {
  value: InboxState;
}

// Fund-level documents are watermarked with the single investor the inbox is currently filtered to.
// With no unambiguous investor selection the name is left undefined -> the backend omits the investor name from the watermark.
const getWatermarkInvestorName = (investors: string[] | undefined): string | undefined =>
  investors?.length === 1 ? investors[0] : undefined;

export const InboxStateContextProvider = ({ value, children }: PropsWithChildren<Props>) => {
  const [selection, setSelection] = useState<GridRowSelectionModel>(() => ({ type: "include", ids: new Set() }));

  const watermarkInvestorName = useMemo(
    () => getWatermarkInvestorName(value.criteria.investors),
    [value.criteria.investors]
  );

  return (
    <InboxStateContext value={{ ...value, selection, setSelection, watermarkInvestorName }}>
      {children}
    </InboxStateContext>
  );
};

export default InboxStateContext;
